import { IonButton, IonButtons, IonIcon, IonTitle, IonToolbar } from '@ionic/react';
import { arrowBack, arrowBackOutline, close, closeOutline, closeCircleOutline, chevronBack, chevronBackOutline } from 'ionicons/icons';
import React, { Children, cloneElement, forwardRef, isValidElement, ReactNode, useRef } from 'react';
import useModalPresentation, { ModalPresentation } from './useModalPresentation';
import './Modal.css';

const navigationIcons = [arrowBack, arrowBackOutline, close, closeOutline, closeCircleOutline, chevronBack, chevronBackOutline];
const isNavigationIcon = (node: ReactNode): boolean => isValidElement<any>(node) && node.type === IonIcon && navigationIcons.includes(node.props.icon || node.props.src);
const classes = (...values: (string | undefined)[]) => values.filter(Boolean).join(' ');

/** Mantém o botão e seu callback originais, alterando apenas apresentação e acessibilidade. */
export function adaptModalToolbar(children: ReactNode, presentation: ModalPresentation): ReactNode {
  return Children.map(children, child => {
    if (!isValidElement<any>(child)) return child;
    if (child.type === React.Fragment) return cloneElement(child, {}, adaptModalToolbar(child.props.children, presentation));
    if (child.type !== IonButtons) return child;
    const controls = Children.toArray(child.props.children);
    const navigation = controls.find(control => isNavigationIcon(control) || (isValidElement<any>(control) && control.type === IonButton && Children.toArray(control.props.children).some(isNavigationIcon)));
    if (!isValidElement<any>(navigation) || controls.length !== 1) return child;
    const previous = child.props['data-modal-navigation'] === 'previous' || navigation.props['data-modal-navigation'] === 'previous';
    const back = previous || presentation === 'fullscreen';
    const label = previous ? navigation.props['aria-label'] || 'Voltar' : back ? 'Voltar' : 'Fechar';
    const icon = back ? arrowBack : closeOutline;
    const replaceIcon = (node: ReactNode) => isValidElement<any>(node) && isNavigationIcon(node) ? cloneElement(node, { icon, src: undefined, 'aria-hidden': true }) : node;
    let button: ReactNode;
    if (isNavigationIcon(navigation)) {
      const { onClick, ...iconProps } = navigation.props;
      button = <IonButton key={back ? 'modal-back' : 'modal-close'} type="button" fill="clear" aria-label={label} onClick={onClick} className="xeguei-toolbar-navigation">
        <IonIcon {...iconProps} icon={icon} src={undefined} aria-hidden="true" />
      </IonButton>;
    } else {
      button = cloneElement(navigation, {
        key: back ? 'modal-back' : 'modal-close',
        'aria-label': label,
        className: classes(navigation.props.className, 'xeguei-toolbar-navigation'),
      }, Children.map(navigation.props.children, replaceIcon));
    }
    return cloneElement(child, {
      slot: back ? 'start' : 'end',
      className: classes(child.props.className, 'xeguei-modal-navigation-group'),
    }, button);
  });
}

function hasHeading(children: ReactNode): boolean {
  return Children.toArray(children).some(child => isValidElement<any>(child) && (
    child.type === IonTitle || isNavigationIcon(child) || hasHeading(child.props.children)
  ));
}

/** Fora de um cabeçalho de modal, o IonToolbar mantém exatamente suas props e filhos. */
const ModalToolbar = forwardRef<HTMLIonToolbarElement, React.ComponentProps<typeof IonToolbar>>((props, forwardedRef) => {
  const ref = useRef<HTMLIonToolbarElement>(null);
  const presentation = useModalPresentation(ref, true);
  return <IonToolbar {...props} ref={element => {
    (ref as React.MutableRefObject<HTMLIonToolbarElement | null>).current = element;
    if (typeof forwardedRef === 'function') forwardedRef(element);
    else if (forwardedRef) (forwardedRef as React.MutableRefObject<HTMLIonToolbarElement | null>).current = element;
  }} className={classes(props.className, presentation && hasHeading(props.children) ? 'xeguei-modal-toolbar' : undefined)}>
    {presentation ? adaptModalToolbar(props.children, presentation) : props.children}
  </IonToolbar>;
});
export default ModalToolbar;
